import React from 'react'
import Icon from '@amzn/meridian/icon'
import alertTokens from '@amzn/meridian-tokens/base/icon/alert'
// The design calls for the clipboard glyph; it is not in the pinned meridian-tokens release,
// so the closest available glyph is used until the token package is bumped.
import documentTokens from '@amzn/meridian-tokens/base/icon/document'
import gridTokens from '@amzn/meridian-tokens/base/icon/grid'
import bellTokens from '@amzn/meridian-tokens/base/icon/bell'
import { ActionCenterPriority, KnownCategory } from '../types'
import { PRIORITY_TONE } from '../constants'
import { isKnownCategory } from '../utils'

const CATEGORY_TOKENS: Readonly<Record<KnownCategory, object>> = {
    OPERATIONAL: documentTokens,
    PLANOGRAM: gridTokens,
    THEFT: alertTokens,
}

/** Icon glyph for a category; unknown categories fall back to a bell. */
export const categoryIconTokens = (category: string | undefined): object =>
    isKnownCategory(category) ? CATEGORY_TOKENS[category] : bellTokens

interface ICategoryIconProps {
    category?: string
    /** When set, the icon is wrapped in a severity-toned circle (card usage). Omit for a plain glyph. */
    priority?: ActionCenterPriority
    label?: string
}

/**
 * Category glyph, optionally inside the severity-coloured circle used on notification cards.
 * Colour comes from the priority (the severity signal), the glyph from the category.
 */
export const CategoryIcon: React.FC<ICategoryIconProps> = ({ category, priority, label }) => {
    const tokens = categoryIconTokens(category)
    if (!priority) {
        return (
            <span className="action-center-category-glyph" data-testid="category-glyph">
                <Icon tokens={tokens}>{label}</Icon>
            </span>
        )
    }
    return (
        <span
            className={`action-center-icon action-center-icon--${PRIORITY_TONE[priority]}`}
            data-testid="category-icon"
            data-tone={PRIORITY_TONE[priority]}
        >
            <Icon tokens={tokens}>{label}</Icon>
        </span>
    )
}
